import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import s from "../modules/TrainerMy.module.css";
import { useT, useI18nReady } from "../components/TextProvider";

/**
 * Страница: Мои тренажёры
 * — Загружает список AI (/api/aikeys/) для отображения названий
 * — Загружает список тренажёров из /api/vazifalar/
 * — Поиск по названию и вопросу (клиентский)
 * — Клиентская пагинация (если сервер не отдаёт paginate)
 * — Кнопки: Просмотр (если есть роут), Редактировать (опционально), Удалить (опционально)
 */

const VAZIFALAR_ENDPOINT =
  process.env.REACT_APP_VAZIFALAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/vazifalar/";

const AIKEYS_ENDPOINT =
  process.env.REACT_APP_AIKEYS_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/aikeys/";

// ===== helpers: auth
function getStoredUser() {
  const ls = window.localStorage.getItem("auth:user");
  const ss = window.sessionStorage.getItem("auth:user");
  const raw = ls ?? ss;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}
function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") || s.getItem("auth:token") || s.getItem("auth:key") ||
    s.getItem("auth:jwt") || s.getItem("auth:auth_token") || null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}
function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME; // "Bearer" | "Token" | "JWT"
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

export default function TrainerMy() {

  const t = useT();
  const ready = useI18nReady();

  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);

  const [aiMap, setAiMap] = useState({}); // id -> { name, img }
  const [allItems, setAllItems] = useState([]); // оригинальные
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Поиск и пагинация
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 8;

  // Загрузка AI
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const token = getAnyTokenFromStorage();
        const res = await fetch(AIKEYS_ENDPOINT, {
          method: "GET",
          headers: { "Content-Type": "application/json", ...(token ? makeAuthHeader(token) : {}) },
          credentials: "include",
        });
        const data = await res.json().catch(() => []);
        const map = {};
        if (Array.isArray(data)) {
          data.forEach((a) => { if (a?.id) map[a.id] = a; });
        }
        if (!abort) setAiMap(map);
      } catch {
        if (!abort) setAiMap({});
      }
    })();
    return () => { abort = true; };
  }, []);

  // Загрузка тренажёров
  useEffect(() => {
    let abort = false;
    (async () => {
      if (!user?.id) { setError("Foydalanuvchi topilmadi. Iltimos, qaytadan tizimga kiring."); setLoading(false); return; }
      setLoading(true); setError("");
      try {
        const token = getAnyTokenFromStorage();

        // Загружаем ВСЕ тренажёры
        const res = await fetch(VAZIFALAR_ENDPOINT, {
          method: "GET",
          headers: { "Content-Type": "application/json", ...(token ? makeAuthHeader(token) : {}) },
          credentials: "include",
        });

        if (!res.ok) throw new Error("Trenajyorlarni yuklashda xatolik");

        const data = await res.json().catch(() => []);
        let arr = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        // НЕ фильтруем по user - показываем всё
        if (!abort) setAllItems(arr);
      } catch (e) {
        if (!abort) setError(e?.message || "Roʻyxatni yuklashda xatolik yuz berdi");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [user?.id]);

  // Клиентская фильтрация/поиск
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return allItems;
    return allItems.filter((it) =>
      String(it?.name || "").toLowerCase().includes(needle) ||
      String(it?.savol || "").toLowerCase().includes(needle)
    );
  }, [allItems, q]);

  // Пагинация
  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const pageClamped = Math.min(Math.max(1, page), pages);
  const slice = filtered.slice((pageClamped - 1) * pageSize, pageClamped * pageSize);

  useEffect(() => { if (pageClamped !== page) setPage(pageClamped); }, [pageClamped]);

  // Удаление (опционально)
  async function handleDelete(id) {
    if (!window.confirm("Trenajorni o'chirmoqchisiz? Bu amal qaytarilib bo'lmaydi.")) return;
    try {
      const token = getAnyTokenFromStorage();
      const res = await fetch(`${VAZIFALAR_ENDPOINT}${id}/`, {
        method: "DELETE",
        headers: { ...(token ? makeAuthHeader(token) : {}) },
        credentials: "include",
      });
      if (!res.ok && res.status !== 204) throw new Error("Server o'chirishni rad etdi");
      // локально уберём
      setAllItems((prev) => prev.filter((x) => String(x.id) !== String(id)));
    } catch (e) {
      alert(e?.message || "O'chirish amalga oshmadi");
    }
  }

  return (
    <div className={s.wrapper}>
      <div className={s.headRow}>
        <h1 className={s.title}>{t('myTren')}</h1>
      </div>

      <div className={s.toolbar}>
        <input
          className={s.search}
          placeholder={t('trainMyPoisk')}
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
        />
      </div>

      {loading && <div className={s.info}>Yuklanmoqda…</div>}
      {error && !loading && <div className={s.error}>{error}</div>}

      {!loading && !error && (
        slice.length ? (
          <div className={s.grid}>
            {slice.map((it) => {
              const ai = aiMap[it.AI] || null;
              return (
                <article key={it.id} className={s.card}>
                  <div className={s.thumbBox}>
                    {it.img ? (
                      <img src={it.img} alt={it.name} className={s.thumb} />
                    ) : (
                      <div className={s.thumbPlaceholder}>IMG</div>
                    )}
                  </div>
                  <div className={s.body}>
                    <h3 className={s.name} title={it.name}>{it.name}</h3>
                    <div className={s.meta}>
                      {ai ? (
                        <span className={s.ai}>AI: {ai.name}</span>
                      ) : (
                        it.AI ? <span className={s.ai}>AI ID: {it.AI}</span> : <span className={s.ai}>AI: —</span>
                      )}
                    </div>
                    {/* <p className={s.excerpt}>
                      {String(it.savol || "").slice(0, 60)}{String(it.savol || "").length > 60 ? "…" : ""}
                    </p> */}
                  </div>
                  <div className={s.row}>
                    {/* Замените роуты на свои */}
                    <Link className={s.btn} to={`/train/${it.id}`}>{t('ochish')}</Link>
                    <Link className={s.btn} to={`/TrainerEdit/${it.id}`}>{t('tahrirlash')}</Link>
                    {/* <button className={s.btnDanger} onClick={() => handleDelete(it.id)}>Удалить</button> */}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className={s.info}>{t('trainMyNone')}</div>
        )
      )}

      {/* Пагинация */}
      {!loading && !error && pages > 1 && (
        <div className={s.pagination} role="navigation" aria-label="pagination">
          <button className={s.pageBtn} disabled={pageClamped === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
          <span className={s.pageInfo}>{pageClamped} / {pages}</span>
          <button className={s.pageBtn} disabled={pageClamped === pages} onClick={() => setPage(p => Math.min(pages, p + 1))}>›</button>
        </div>
      )}
    </div>
  );
}

